import ngCookies from 'angular-cookies';
import angularJwt from 'angular-jwt';
import uiRouter from '@uirouter/angularjs';

import { NativePlatformServiceModule } from 'services/access/nativePlatform/nativePlatform.service';
import { LaunchDarklyServiceModule } from 'services/access/launchDarkly/launchDarkly.service';
import { SessionTimeoutModalServiceModule } from 'components/sessionTimeoutModal/sessionTimeoutModal.service';
import { ConciergeAuthServiceModule } from 'services/auth/conciergeAuth.service';
import { AUTH0_TENANT_CLAIM } from 'app/constants';
import { AuthTokenServiceModule } from './authToken.service';

export const AuthServiceModule = angular
    .module('app.services.auth.authService', [
        ngCookies,
        angularJwt,
        uiRouter,
        NativePlatformServiceModule,
        LaunchDarklyServiceModule,
        ConciergeAuthServiceModule,
        SessionTimeoutModalServiceModule,
        AuthTokenServiceModule
    ])
    .service('authService', (
        $cookies,
        $location,
        $window,
        $document,
        $q,
        $http,
        $state,
        jwtHelper,
        conciergeAuthService,
        nativePlatformService,
        launchDarklyService
    ) => {
        let config = {
            auth0Domain: process.env.AUTH0_DOMAIN,
            auth0ClientId: process.env.AUTH0_CLIENT_ID,
            auth0Audience: process.env.AUTH0_AUDIENCE,
            registrationApiUrl: `${process.env.BASE_API_URL}/registration`,
            rootApiUrl: process.env.BASE_API_URL
        };

        function getRootDomain(hostname) {
            let parts = hostname.split('.');
            let hasSubdomain = parts.length === 3;
            return hasSubdomain ? `${parts[1]}.${parts[2]}` : hostname;
        }

        function clearUser() {
            $window.localStorage.removeItem('user');
            $cookies.remove('id_token');
            $cookies.remove('access_token');
        }

        function isGoogleUser(decodedIdToken) {
            if (!decodedIdToken) {
                return false;
            }
            const userId = decodedIdToken.user_id || decodedIdToken.sub;
            if (!userId) return false;
            const userIdProvider = userId.split('|')[0];
            return userIdProvider === 'google-oauth2';
        }

        function getTenant(decodedIdToken) {
            if (!decodedIdToken) return null;
            return decodedIdToken.tenant || decodedIdToken[AUTH0_TENANT_CLAIM] || null;
        }

        function hasExistingStore(decodedIdToken) {
            if (!decodedIdToken || !decodedIdToken.email) {
                return $q.reject('no token or email');
            }
            return checkEmail(decodedIdToken.email).then((response) => {
                return response.data;
            });
        }

        function register(user) {
            return $http.post(`${config.registrationApiUrl}/stores`, user)
                .then((response) => {
                    if (response.data.tenantId) {
                        // fall back to email/password if token was not available
                        return this.login(user.email, user.password).then(() => {
                            return { tenantId: response.data.tenantId };
                        });
                    }
                    return $q.reject({ data: response.data });
                });
        }

        function registerViaGoogle(decodedIdToken) {
            const user = {
                auth0UserId: decodedIdToken.user_id || decodedIdToken.sub,
                username: decodedIdToken.name,
                email: decodedIdToken.email,
                termsAndConditionsAccepted: true,
                firstName: decodedIdToken.given_name,
                lastName: decodedIdToken.family_name,
                planId: process.env.LIST_PERSONAL_PLAN_ID
            };
            return launchDarklyService.getFeature('create-salesforce-lead')
                .then((getsSalesforceFeature) => {
                    if (getsSalesforceFeature) {
                        user.createSalesforceLead = true;
                        user.salesforce = {
                            Email: decodedIdToken.email,
                            FirstName: decodedIdToken.given_name,
                            LastName: decodedIdToken.family_name,
                            Lead_Source_Description__C: 'Google Social Signup',
                            Device_Type__c: nativePlatformService.isMobileUserAgent ? 'mobile' : 'desktop',
                            SocialSignUp__c: true,
                            SocialSignUpNetwork__c: 'google-oauth2',
                            productVersion__c: 'v2',
                            GA_Source__c: 'Google Social Signup',
                            GA_Campaign__c: 'Google Social Signup',
                            GA_Medium__c: 'Google Social Signup',
                            Marketing_1__c: 'Google Social Signup',
                            Marketing_2__c: 'Google Social Signup',
                            Marketing_3__c: 'Google Social Signup'
                        };
                    }
                    return $http.post(`${config.registrationApiUrl}/stores`, user);
                })
                .catch(() => {
                    return $http.post(`${config.registrationApiUrl}/stores`, user);
                });
        }

        function linkExistingGoogleUser(decodedIdToken) {
            const auth0UserId = decodedIdToken.user_id || decodedIdToken.sub;
            const email = decodedIdToken.email;
            return $http.put(`${config.rootApiUrl}/admin/users/${auth0UserId}/${email}/linkgoogleaccount`);
        }

        function resetPassword(user) {
            return $http.post(`${config.rootApiUrl}/admin/passwords/reset`, user, {
                transformResponse: angular.identity
            });
        }

        function checkEmail(email) {
            return $http.get(`${config.rootApiUrl}/users?email=${email}`);
        }

        function isImpersonator() {
            return conciergeAuthService.isImpersonator();
        }

        function isCookiesEnabled() {
            let cookieEnabled = $window.navigator.cookieEnabled;
            if (typeof $window.navigator.cookieEnabled === 'undefined' && !cookieEnabled) {
                $document.cookie = 'testcookie';
                cookieEnabled = $document.cookie.indexOf('testcookie') !== -1;
            }
            return cookieEnabled;
        }

        function getTokensFromUrlHash(hash) {
            if (hash && hash.length > 0) {
                const tokenDataArray = hash.split('&').map((i) => {
                    const parts = i.split('=');
                    const partsObj = {};
                    partsObj[parts[0]] = parts[1];
                    return partsObj;
                });

                if (tokenDataArray && tokenDataArray.length > 0) {
                    const accessToken = unwrapArray(tokenDataArray.filter((tokenObj) => {
                        return tokenObj.access_token;
                    }));

                    const idToken = unwrapArray(tokenDataArray.filter((i) => {
                        return i.id_token;
                    }));

                    if (accessToken && idToken) {
                        const tokens = {};
                        tokens.access_token = accessToken.access_token;
                        tokens.id_token = idToken.id_token;
                        return tokens;
                    }
                }
            }

            function unwrapArray(arr) {
                if (arr.length && arr.length === 1) return arr[0];
            }

            return null;
        }

        function hasTokens(tokens) {
            return tokens.id_token && tokens.access_token;
        }

        return {
            checkEmail,
            clearUser,
            config,
            getRootDomain,
            getTenant,
            getTokensFromUrlHash,
            hasExistingStore,
            hasTokens,
            isCookiesEnabled,
            isGoogleUser,
            isImpersonator,
            linkExistingGoogleUser,
            register,
            registerViaGoogle,
            resetPassword
        };
    })
    .name;



// WEBPACK FOOTER //
// ./src/app/services/auth/auth.service.js